import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { catchError, of, switchMap, tap } from "rxjs";

import { PAGE_URL } from "@/data_source/constants";
import {
  currentApiEnvironmentId,
  type ApiEnvironmentId
} from "@/data_source/constants/apiEnvironment";
import { ENABLE_API_ENV_SELECTOR } from "@/data_source/constants/env";
import { signIn } from "@/data_source/services/APISignIn";
import { getUserMe } from "@/data_source/services/UserService";
import { redirectToPage, useAppNavigator } from "@/router/redirections";
import { type RouteAppComponentProps } from "@/router/routes";
import { AppLoadingStore } from "@/stores/app-loading-store";
import { shouldRedirectToAccessNotConfigured } from "@/utils/auth/guards_builder";
import { setPageTitle } from "@/utils/hook";
import { RequestError } from "@/utils/request/RequestError";

import { switchApiEnvironment } from "./switchApiEnvironment";
import { AppSignInView } from "./view";

function _AppSignIn(props: RouteAppComponentProps) {
  const [userData, setUserData] = useState<{
    email?: string;
    password?: string;
    remindMe?: boolean;
  }>({});

  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [apiEnvironment, setApiEnvironment] = useState<ApiEnvironmentId>(() =>
    currentApiEnvironmentId()
  );
  const navigate = useAppNavigator();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleApiEnvironmentChange = (next: ApiEnvironmentId) => {
    switchApiEnvironment(next);
    setApiEnvironment(next);
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setUserData((prev) => ({
        ...prev,
        email: savedEmail,
        remindMe: true
      }));
    }
  }, []);

  const handleRememberMeChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = ev.target.checked;
    setUserData((prev) => ({ ...prev, remindMe: isChecked }));

    if (isChecked && userData.email) {
      localStorage.setItem("rememberedEmail", userData.email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }
  };

  const login = useCallback(() => {
    if (!userData.email || !userData.password) return;

    const subscription = of(true)
      .pipe(
        tap(() => AppLoadingStore.getInstance().add(true)),
        switchMap(() =>
          signIn({
            username: userData.email ?? "",
            password: userData.password ?? ""
          }).pipe(
            tap(() => {
              localStorage.setItem(
                "email",
                JSON.stringify({ email: userData.email ?? "" })
              );
            }),
            catchError((err) => {
              console.error(err);
              switch (true) {
                case err instanceof RequestError && err.status === 429:
                  setErrorMessage(
                    "Too many login attempts. Please try again later."
                  );
                  break;
                default:
                  setErrorMessage(
                    "Error to login, your account or password was not correct"
                  );
              }
              AppLoadingStore.getInstance().add(false);
              return of(null);
            })
          )
        ),
        switchMap((signInResponse) => {
          if (!signInResponse?.data) return of(false);

          return getUserMe().pipe(
            tap((userData) => {
              const user = userData?.data;

              if (!user?.is_verified) {
                setErrorMessage("Your account is not active");
                AppLoadingStore.getInstance().add(true);
                redirectToPage(PAGE_URL.SIGN_UP_CODE_CONFIRMATION);
                return;
              }
              const host =
                user.companys_sites?.find(
                  (site) => site.domain === window.location.hostname
                ) ?? user.companys_sites?.[0];

              if (!host.configuration?.registration_completed) {
                setErrorMessage(
                  "Your account does not have any projects configured"
                );
                AppLoadingStore.getInstance().add(true);
                redirectToPage(PAGE_URL.SIGN_UP_CHECKBOX, host, navigate);
                return;
              }

              if (shouldRedirectToAccessNotConfigured(user)) {
                AppLoadingStore.getInstance().add(false);
                redirectToPage(PAGE_URL.ACCESS_NOT_CONFIGURED, host, navigate);
                return;
              }

              setErrorMessage("");
              AppLoadingStore.getInstance().add(true);
              redirectToPage(PAGE_URL.HOME, host, navigate);
            }),
            catchError((err) => {
              console.error("Error fetching user data:", err);
              let backendMessage = "Failed to fetch user details.";

              if (err?.message.includes(t("userInactive"))) {
                setErrorMessage(t("Inactive or deleted user"));
                localStorage.setItem("rememberedEmail", userData.email ?? "");
                navigate(PAGE_URL.USER_INACTIVE);
              } else if (err?.message) {
                const match = err.message.match(/DETAIL\s+(.+)/);
                if (match?.[1]) {
                  backendMessage = match[1].trim();
                } else {
                  backendMessage = err.message;
                }
              }

              setErrorMessage(backendMessage);
              return of(false);
            })
          );
        })
      )
      .subscribe();

    return () => {
      if (!subscription.closed) subscription.unsubscribe();
    };
  }, [navigate, userData]);

  useEffect(() => {
    setPageTitle(window)(props.pageName)();
  }, []);

  return (
    <AppSignInView
      {...userData}
      errorMessage={errorMessage}
      apiEnvironment={ENABLE_API_ENV_SELECTOR ? apiEnvironment : undefined}
      onApiEnvironmentChange={handleApiEnvironmentChange}
      onChange={(ev) => setUserData((prev) => ({ ...prev, ...ev }))}
      onLoginClick={login}
      togglePasswordVisibility={togglePasswordVisibility}
      showPassword={showPassword}
      handleRememberMeChange={handleRememberMeChange}
    />
  );
}

const AppSignIn = _AppSignIn;

export default AppSignIn;
